<template>
  <div class="ocr-page-section">
    <OcrPageShimmer v-if="loadingReports" variant="reports" />
    <template v-else>
      <div class="ocr-report-workspace">
        <aside class="ocr-report-sidebar ocr-panel">
          <nav class="ocr-process-nav" aria-label="Process Reports">
            <button
              v-for="item in processViews"
              :key="item.value"
              type="button"
              class="ocr-process-nav-item"
              :class="{ 'ocr-process-nav-item--active': reportsTab === item.value }"
              @click="reportsTab = item.value"
            >
              {{ item.label }}
            </button>
          </nav>
          <div class="ocr-field">
            <label class="profile-menu-label">Report</label>
            <v-select
              v-model="selectedReport"
              :items="reportSelectItems"
              variant="outlined"
              density="comfortable"
              hide-details
              menu-icon="mdi-chevron-down"
              class="ocr-report-report-select"
              @update:model-value="selectReport"
            />
          </div>
          <div v-if="switchingReport" class="ocr-report-nav-shimmer" aria-hidden="true">
            <OcrSkLine width="72%" height="12px" />
            <OcrSkLine v-for="n in 6" :key="'nav-sk-' + n" width="100%" height="36px" round />
          </div>
          <template v-else>
            <p v-if="mappingSummary" class="ocr-mapping-summary">
              Mapped {{ mappingSummary.mapped_tables }} / {{ mappingSummary.total_tables }} tables
            </p>
            <OcrReportSidebarNav
              :filename="loadedReport"
              :tables="tables"
              :selected-table-index="selectedTableIndex"
              :page-index="previewPageIndex"
              @select-table="selectedTableIndex = $event"
              @go-page="onGoPage"
              @open-map-tab="reportsTab = 'map'"
            />
          </template>
        </aside>

        <div class="ocr-report-main">
          <div class="ocr-tabbed-stack">
            <div v-if="switchingReport" class="ocr-report-switch-shimmer" aria-busy="true" aria-live="polite">
              <template v-if="reportsTab === 'reports'">
                <div class="ocr-report-switch-split">
                  <OcrSkBlock height="100%" min-height="480px" />
                  <OcrSkBlock height="100%" min-height="480px" />
                </div>
              </template>
              <template v-else-if="reportsTab === 'map'">
                <OcrSkLine width="100%" height="52px" round />
                <OcrSkLine width="100%" height="44px" round />
                <div class="ocr-report-switch-split">
                  <OcrSkBlock height="100%" min-height="420px" />
                  <OcrSkBlock height="100%" min-height="420px" />
                </div>
              </template>
              <OcrTableSectionSkeleton
                v-else-if="reportsTab === 'tables'"
                title="Report tables"
                :columns="['Table', 'Type', 'Status']"
                :row-count="6"
              />
              <div v-else class="ocr-report-switch-stack">
                <OcrSkLine width="180px" height="16px" />
                <OcrSkBlock height="280px" />
              </div>
            </div>
            <v-window v-else v-model="reportsTab" class="ocr-tab-window">
              <v-window-item value="reports">
                <div class="ocr-report-picker">
                  <div class="ocr-report-picker-head">
                    <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :disabled="!loadedReport" @click="saveReportOpen = true">
                      Save Report
                    </v-btn>
                    <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :loading="refreshingReports" @click="loadReports(false)">
                      Refresh reports
                    </v-btn>
                  </div>
                  <v-expansion-panels v-model="filtersOpen" class="ocr-expanders">
                    <v-expansion-panel>
                      <v-expansion-panel-title>Filters</v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <div class="ocr-report-filters-grid">
                          <div class="ocr-field">
                            <label class="profile-menu-label">Select Lab(s)</label>
                            <v-select v-model="labFilter" :items="labOptions" multiple chips closable-chips clearable density="compact" variant="outlined" hide-details :menu-props="selectMenuProps" />
                          </div>
                          <div class="ocr-field">
                            <label class="profile-menu-label">Select Asset(s)</label>
                            <v-select v-model="assetFilter" :items="assetOptions" multiple chips closable-chips clearable density="compact" variant="outlined" hide-details :menu-props="selectMenuProps" />
                          </div>
                          <div class="ocr-field">
                            <label class="profile-menu-label">Select Field(s)</label>
                            <v-select v-model="fieldFilter" :items="fieldOptions" multiple chips closable-chips clearable density="compact" variant="outlined" hide-details :menu-props="selectMenuProps" />
                          </div>
                          <div class="ocr-field">
                            <label class="profile-menu-label">Select Well(s)</label>
                            <v-select v-model="wellFilter" :items="wellOptions" multiple chips closable-chips clearable density="compact" variant="outlined" hide-details :menu-props="selectMenuProps" />
                          </div>
                          <div class="ocr-field">
                            <label class="profile-menu-label">Select Report(s)</label>
                            <v-select v-model="reportNameFilter" :items="reportNameOptions" multiple chips closable-chips clearable density="compact" variant="outlined" hide-details :menu-props="selectMenuProps" />
                          </div>
                        </div>
                        <p v-if="activeFilterCount" class="ocr-empty-cell">{{ activeFilterCount }} filter(s) applied</p>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                  <v-dialog v-model="saveReportOpen" max-width="720">
                    <v-card class="ocr-dialog-card">
                      <v-card-title class="ocr-dialog-title">
                        <span>Save Report</span>
                        <v-btn class="ocr-dialog-close" icon="mdi-close" variant="text" aria-label="Close" @click="saveReportOpen = false" />
                      </v-card-title>
                      <form @submit.prevent="saveDetails">
                        <v-card-text class="ocr-dialog-body">
                          <div class="ocr-report-details-grid">
                            <div class="ocr-field">
                              <label class="profile-menu-label">reportDocumentID</label>
                              <v-text-field :model-value="reportDetails.report_name" density="compact" variant="outlined" hide-details readonly />
                            </div>
                            <div class="ocr-field">
                              <label class="profile-menu-label">updated</label>
                              <v-text-field :model-value="reportDetails.updated" density="compact" variant="outlined" hide-details readonly />
                            </div>
                            <div class="ocr-field">
                              <label class="profile-menu-label">asset</label>
                              <v-text-field v-model="reportDetails.asset" density="compact" variant="outlined" hide-details />
                            </div>
                            <div class="ocr-field">
                              <label class="profile-menu-label">field</label>
                              <v-text-field v-model="reportDetails.field" density="compact" variant="outlined" hide-details />
                            </div>
                            <div class="ocr-field">
                              <label class="profile-menu-label">reservoir</label>
                              <v-text-field v-model="reportDetails.reservoir" density="compact" variant="outlined" hide-details />
                            </div>
                            <div class="ocr-field">
                              <label class="profile-menu-label">well</label>
                              <v-text-field v-model="reportDetails.well" density="compact" variant="outlined" hide-details />
                            </div>
                            <div class="ocr-field">
                              <label class="profile-menu-label">analysisLaboratory</label>
                              <v-text-field v-model="reportDetails.lab" density="compact" variant="outlined" hide-details />
                            </div>
                            <div class="ocr-field">
                              <label class="profile-menu-label">Fluid Type</label>
                              <v-select
                                v-model="reportDetails.fluid_type"
                                :items="fluidTypes"
                                :menu-props="{ contentClass: 'ocr-select-menu' }"
                                density="compact"
                                variant="outlined"
                                hide-details
                              />
                            </div>
                          </div>
                        </v-card-text>
                        <v-card-actions class="ocr-dialog-actions">
                          <v-spacer />
                          <v-btn variant="text" class="ocr-toolbar-btn" @click="saveReportOpen = false">Cancel</v-btn>
                          <v-btn type="submit" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="savingDetails" :disabled="!loadedReport">
                            Save Report
                          </v-btn>
                        </v-card-actions>
                      </form>
                    </v-card>
                  </v-dialog>
                </div>
                <div
                  v-if="loadedReport"
                  ref="reportSplit"
                  class="ocr-report-split"
                  :class="{ 'ocr-report-split--doc-collapsed': docCollapsed }"
                  :style="splitStyle"
                >
                  <OcrReportPagePreview
                    v-model:page-index="previewPageIndex"
                    :filename="loadedReport"
                    :max-page="maxPageIndex"
                    :highlight-tables="true"
                    :active-table-index="selectedTableIndex"
                  />
                  <div
                    v-if="!docCollapsed"
                    class="ocr-split-handle"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize page and document preview"
                    @pointerdown="onSplitDown"
                    @pointermove="onSplitMove"
                    @pointerup="onSplitUp"
                    @pointercancel="onSplitUp"
                  />
                  <OcrReportDocument
                    v-model:page-index="previewPageIndex"
                    :filename="loadedReport"
                    @update:collapsed="docCollapsed = $event"
                  />
                </div>
              </v-window-item>

              <v-window-item value="tables">
                <OcrReportTablesPanel
                  :filename="loadedReport"
                  :tables="tables"
                  :table-types="tableTypes"
                  :selected-table-index="selectedTableIndex"
                  :table-detail="selectedTableDetail"
                  :page-index="previewPageIndex"
                  :max-page-index="maxPageIndex"
                  @select-table="selectedTableIndex = $event"
                  @go-page="onGoPage"
                  @added="onTableAdded"
                  @updated="onTableUpdated"
                />
              </v-window-item>

              <v-window-item value="map">
                <OcrReportMapPanel
                  v-model:page-index="previewPageIndex"
                  :filename="loadedReport"
                  :table-index="selectedTableIndex"
                  :table-types="tableTypes"
                  :max-page-index="maxPageIndex"
                  :refresh-token="tableRefresh"
                  :start-step="mapStartStep"
                  @updated="onTableUpdated"
                />
              </v-window-item>

              <v-window-item value="samples">
                <OcrReportSamples :filename="loadedReport" />
              </v-window-item>
            </v-window>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrPageShimmer from "@/components/layout/OCR/OcrPageShimmer.vue";
import OcrSkBlock from "@/components/layout/OCR/OcrSkBlock.vue";
import OcrSkLine from "@/components/layout/OCR/OcrSkLine.vue";
import OcrTableSectionSkeleton from "@/components/layout/OCR/OcrTableSectionSkeleton.vue";
import OcrReportDocument from "@/components/layout/OCR/OcrReportDocument.vue";
import OcrReportMapPanel from "@/components/layout/OCR/OcrReportMapPanel.vue";
import OcrReportPagePreview from "@/components/layout/OCR/OcrReportPagePreview.vue";
import OcrReportSidebarNav from "@/components/layout/OCR/OcrReportSidebarNav.vue";
import OcrReportSamples from "@/components/layout/OCR/OcrReportSamples.vue";
import OcrReportTablesPanel from "@/components/layout/OCR/OcrReportTablesPanel.vue";
import { ocrNotifyMixin } from "@/components/layout/OCR/OcrSnackbar.vue";

export default {
  name: "OcrReports",
  mixins: [ocrNotifyMixin],
  components: {
    OcrPageShimmer,
    OcrSkBlock,
    OcrSkLine,
    OcrTableSectionSkeleton,
    OcrReportDocument,
    OcrReportMapPanel,
    OcrReportPagePreview,
    OcrReportSidebarNav,
    OcrReportSamples,
    OcrReportTablesPanel,
  },
  data() {
    return {
      loadingReports: true,
      switchingReport: false,
      reportLoadToken: 0,
      refreshingReports: false,
      reportRows: [],
      filtersOpen: 0,
      saveReportOpen: false,
      labFilter: [],
      assetFilter: [],
      fieldFilter: [],
      wellFilter: [],
      reportNameFilter: [],
      selectMenuProps: { contentClass: "ocr-select-menu" },
      reportsTab: "reports",
      mapStartStep: "",
      processViews: [
        { value: "reports", label: "Reports" },
        { value: "tables", label: "Report Tables" },
        { value: "map", label: "Map Report Tables" },
        { value: "samples", label: "Manage Samples" },
      ],
      reports: [],
      selectedReport: null,
      loadedReport: null,
      selectError: "",
      selectedTableIndex: null,
      previewPageIndex: 0,
      tables: [],
      tableTypes: [],
      selectedTableDetail: null,
      mappingSummary: null,
      navPageCount: 0,
      docCollapsed: false,
      pagePanePercent: 58,
      splitDrag: false,
      fluidTypes: ["Unknown", "Dry Gas", "Wet Gas", "Gas Condensate", "Volatile Oil", "Black Oil", "Heavy Oil"],
      reportDetails: { report_name: "", updated: "", asset: "", field: "", reservoir: "", well: "", lab: "", fluid_type: "Unknown" },
      savingDetails: false,
      detailsMessage: "",
      tableRefresh: 0,
    };
  },
  computed: {
    maxPageIndex() {
      if (this.navPageCount > 0) return this.navPageCount - 1;
      if (!this.tables.length) return 0;
      return Math.max(...this.tables.map((t) => t.page ?? 0));
    },
    splitStyle() {
      return { "--ocr-page-pane": `${this.pagePanePercent}%` };
    },
    rowsAfterLab() {
      return this.rowsMatching(this.reportRows, { lab: true });
    },
    rowsAfterAsset() {
      return this.rowsMatching(this.reportRows, { lab: true, asset: true });
    },
    rowsAfterField() {
      return this.rowsMatching(this.reportRows, { lab: true, asset: true, field: true });
    },
    rowsAfterWell() {
      return this.rowsMatching(this.reportRows, { lab: true, asset: true, field: true, well: true });
    },
    labOptions() {
      return this.uniqueValues(this.reportRows, "analysisLaboratory");
    },
    assetOptions() {
      return this.uniqueValues(this.rowsAfterLab, "asset");
    },
    fieldOptions() {
      return this.uniqueValues(this.rowsAfterAsset, "field");
    },
    wellOptions() {
      return this.uniqueValues(this.rowsAfterField, "well");
    },
    reportNameOptions() {
      return this.uniqueValues(this.rowsAfterWell, "reportDocumentID");
    },
    filteredReportRows() {
      return this.rowsMatching(this.reportRows, { lab: true, asset: true, field: true, well: true, report: true });
    },
    reportSelectItems() {
      const names = this.filteredReportRows.map((row) => row.reportDocumentID).filter(Boolean);
      if (this.loadedReport && !names.includes(this.loadedReport)) names.unshift(this.loadedReport);
      return names.length ? names : this.reports;
    },
    activeFilterCount() {
      return this.labFilter.length + this.assetFilter.length + this.fieldFilter.length + this.wellFilter.length + this.reportNameFilter.length;
    },
  },
  watch: {
    selectError(message) {
      if (message) this.ocrToast(message, "error");
    },
    detailsMessage(message) {
      if (!message) return;
      const variant = message === "Report saved." ? "success" : "error";
      this.ocrToast(message, variant);
      if (variant === "success") this.saveReportOpen = false;
    },
    selectedTableIndex: {
      handler(idx) {
        if (idx == null || !this.selectedReport) {
          this.selectedTableDetail = null;
          return;
        }
        const t = this.tables.find((x) => x.index === idx);
        if (t?.page != null) this.previewPageIndex = t.page;
        this.loadTableDetail(idx);
      },
    },
  },
  async mounted() {
    await Promise.all([this.loadReports(), this.loadTableTypes()]);
    await this.applyQueryNavigation();
  },
  methods: {
    async loadTableTypes() {
      try {
        const { data } = await ocrApi.get("api/v1/reports/meta/table-types");
        this.tableTypes = data.table_types || [];
      } catch {
        this.tableTypes = [];
      }
    },
    matchReportName(stem) {
      if (!stem) return null;
      const exact = this.reports.find((r) => r === stem);
      if (exact) return exact;
      const withPdf = this.reports.find((r) => r === `${stem}.pdf`);
      if (withPdf) return withPdf;
      return this.reports.find((r) => String(r).split(".")[0] === stem) || null;
    },
    async applyQueryNavigation() {
      const q = this.$route.query || {};
      const stem = q.file ? String(q.file) : "";
      const tab = q.tab ? String(q.tab) : "";
      if (tab === "review") {
        this.reportsTab = "map";
        this.mapStartStep = "review";
      } else if (["reports", "tables", "map", "samples"].includes(tab)) {
        this.reportsTab = tab;
        this.mapStartStep = "";
      }
      const matched = this.matchReportName(stem);
      if (matched) {
        this.selectedReport = matched;
        await this.selectReport(matched);
      }
      if (q.page != null && Number(q.page) >= 0) {
        const page = Number(q.page);
        this.previewPageIndex = page;
        const tableNum = q.table != null ? Number(q.table) : null;
        const byTable = this.tables.find(
          (t) => t.page === page && (tableNum == null || t.page_table_number === tableNum || t.index === tableNum),
        );
        const onPage = byTable || this.tables.find((t) => t.page === page);
        if (onPage) this.selectedTableIndex = onPage.index;
      }
    },
    uniqueValues(rows, key) {
      const values = new Set();
      (rows || []).forEach((row) => {
        const value = row?.[key];
        if (value != null && String(value).trim() !== "") values.add(String(value));
      });
      return [...values].sort((a, b) => a.localeCompare(b));
    },
    rowsMatching(rows, which) {
      return (rows || []).filter((row) => {
        if (which.lab && this.labFilter.length && !this.labFilter.includes(row.analysisLaboratory)) return false;
        if (which.asset && this.assetFilter.length && !this.assetFilter.includes(row.asset)) return false;
        if (which.field && this.fieldFilter.length && !this.fieldFilter.includes(row.field)) return false;
        if (which.well && this.wellFilter.length && !this.wellFilter.includes(row.well)) return false;
        if (which.report && this.reportNameFilter.length && !this.reportNameFilter.includes(row.reportDocumentID)) return false;
        return true;
      });
    },
    onPickReport(name, checked) {
      if (checked) {
        this.selectedReport = name;
        this.selectReport(name);
        return;
      }
      if (this.selectedReport === name) this.selectedReport = null;
      if (this.loadedReport === name) {
        this.loadedReport = null;
        this.tables = [];
        this.mappingSummary = null;
        this.selectedTableIndex = null;
        this.selectedTableDetail = null;
      }
    },
    async loadReports(initial = true) {
      if (initial) this.loadingReports = true;
      else this.refreshingReports = true;
      try {
        const { data } = await ocrApi.get("api/v1/reports");
        this.reports = data.reports || [];
        this.reportRows = data.rows?.length
          ? data.rows
          : (data.reports || []).map((name) => ({
              reportDocumentID: name,
              asset: "",
              field: "",
              well: "",
              analysisLaboratory: "",
              updated: "",
            }));
        if (initial && data.selected) {
          this.selectedReport = data.selected;
          await this.selectReport(data.selected);
        }
      } finally {
        this.loadingReports = false;
        this.refreshingReports = false;
      }
    },
    onSplitDown(event) {
      if (event.button !== 0) return;
      this.splitDrag = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onSplitMove(event) {
      if (!this.splitDrag) return;
      const split = this.$refs.reportSplit;
      const rect = split?.getBoundingClientRect();
      if (!rect?.width) return;
      const percent = ((event.clientX - rect.left) / rect.width) * 100;
      this.pagePanePercent = Math.min(70, Math.max(30, Math.round(percent)));
    },
    onSplitUp() {
      this.splitDrag = false;
    },
    onGoPage(page) {
      this.previewPageIndex = page;
      const onPage = this.tables.find((t) => t.page === page);
      if (onPage && this.selectedTableIndex !== onPage.index) {
        this.selectedTableIndex = onPage.index;
      }
    },
    async selectReport(name) {
      if (!name) return;
      const changed = this.loadedReport !== name;
      const token = ++this.reportLoadToken;
      this.selectError = "";
      if (changed) this.switchingReport = true;
      try {
        const { data } = await ocrApi.post("api/v1/reports/select", { filename: name });
        if (token !== this.reportLoadToken) return;
        if (changed) {
          this.selectedTableIndex = null;
          this.previewPageIndex = 0;
          this.selectedTableDetail = null;
          this.tables = [];
          this.mappingSummary = null;
        }
        this.loadedReport = name;
        await this.refreshTables(name, data.tables);
        if (token !== this.reportLoadToken) return;
        await this.loadNavMeta(name);
        await this.loadDetails(name);
      } catch (e) {
        if (token !== this.reportLoadToken) return;
        this.selectError = formatOcrApiError(e, "Could not open this report.");
        if (this.loadedReport && this.selectedReport !== this.loadedReport) {
          this.selectedReport = this.loadedReport;
        }
      } finally {
        if (token === this.reportLoadToken) this.switchingReport = false;
      }
    },
    async loadNavMeta(name) {
      try {
        const { data } = await ocrApi.get(`api/v1/reports/${encodeURIComponent(name)}/navigation`);
        this.navPageCount = data.page_count || 0;
      } catch {
        this.navPageCount = 0;
      }
    },
    async refreshTables(name, fallbackTables) {
      try {
        const detail = await ocrApi.get(`api/v1/reports/${encodeURIComponent(name)}/tables`);
        this.tables = (detail.data.tables || []).map((t, index) => ({
          index: t.index ?? index,
          page: t.page,
          table_type: t.table_type,
          page_table_number: t.page_table_number,
          status: t.table_status === "Ignore"
            ? "ignored"
            : t.table_type && !["Unknown", "Select Table Type", "Ignore"].includes(t.table_type)
              ? "mapped"
              : "unmapped",
        }));
      } catch {
        this.tables = fallbackTables || [];
      }
      if (this.tables.length && this.selectedTableIndex == null) {
        this.selectedTableIndex = this.tables[0].index;
      }
      const map = await ocrApi.get(`api/v1/reports/${encodeURIComponent(name)}/mapping/summary`);
      this.mappingSummary = map.data;
    },
    async loadTableDetail(index) {
      if (!this.selectedReport) return;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.selectedReport)}/tables/${index}`,
        );
        this.selectedTableDetail = data;
      } catch {
        this.selectedTableDetail = null;
      }
    },
    async loadDetails(name) {
      this.detailsMessage = "";
      if (!name) return;
      try {
        const { data } = await ocrApi.get(`api/v1/reports/${encodeURIComponent(name)}/details`);
        this.reportDetails = {
          report_name: data.report_name || name,
          updated: data.updated || "",
          asset: data.asset || "",
          field: data.field || "",
          reservoir: data.reservoir || "",
          well: data.well || "",
          lab: data.lab || "",
          fluid_type: data.fluid_type || "Unknown",
        };
        if (Array.isArray(data.fluid_types) && data.fluid_types.length) this.fluidTypes = data.fluid_types;
      } catch (e) {
        this.detailsMessage = formatOcrApiError(e, "Could not load report details.");
      }
    },
    async saveDetails() {
      if (!this.loadedReport) return;
      this.savingDetails = true;
      this.detailsMessage = "";
      try {
        const { data } = await ocrApi.put(
          `api/v1/reports/${encodeURIComponent(this.loadedReport)}/details`,
          this.reportDetails,
        );
        this.reportDetails.fluid_type = data.fluid_type || this.reportDetails.fluid_type;
        this.detailsMessage = "Report saved.";
      } catch (e) {
        this.detailsMessage = formatOcrApiError(e, "Could not save the report.");
      } finally {
        this.savingDetails = false;
      }
    },
    async onTableAdded(index) {
      if (!this.selectedReport) return;
      await this.selectReport(this.selectedReport);
      if (index != null) this.selectedTableIndex = index;
    },
    async onTableUpdated() {
      if (!this.selectedReport) return;
      await this.selectReport(this.selectedReport);
      await this.loadNavMeta(this.selectedReport);
      if (this.selectedTableIndex != null) {
        await this.loadTableDetail(this.selectedTableIndex);
      }
      this.tableRefresh += 1;
    },
  },
};
</script>
